import { Tag } from 'components/TagsAddInput/TagsAddInput'

import { apiErrorMessage } from 'utils/apiErrorMessage'

import { DataService } from './DataService'

export interface TagAPI {
  uuid: string
  name: string
  color?: string
}

// Função para adicionar # na cor se não tiver
const addHashToColor = (color: string): string => {
  if (!color) return color
  return color.startsWith('#') ? color : `#${color}`
}

// Função para remover # da cor antes de enviar para a API
const removeHashFromColor = (color: string): string => {
  if (!color) return color
  return color.startsWith('#') ? color.substring(1) : color
}

// Função para converter TagAPI para Tag (compatibilidade com componentes)
const convertTagAPIToTag = (tagAPI: TagAPI): Tag => {
  // Verificar se tagAPI tem os campos necessários
  if (!tagAPI || !tagAPI.uuid || !tagAPI.name) {
    console.error('TagAPI inválida:', tagAPI)
    return {
      id: tagAPI?.uuid || 'unknown',
      name: tagAPI?.name || 'Tag sem nome',
      color: tagAPI?.color ? addHashToColor(tagAPI.color) : undefined
    }
  }

  const tag = {
    id: tagAPI.uuid,
    name: tagAPI.name,
    color: tagAPI.color ? addHashToColor(tagAPI.color) : undefined
  }

  return tag
}

export interface TagsResponse {
  data: Tag[]
  total?: number
  page?: number
  per_page?: number
}

export interface CreateTagRequest {
  name: string
  color?: string
}

export interface CreateTagWithLinkRequest {
  name: string
  color?: string
  taggable_type:
    | 'Customer'
    | 'Billing'
    | 'Recurrence'
    | 'ProductLink'
    | 'Product'
    | 'AccountsPayable'
    | 'AccountsPayableRecurrence'
    | 'Installment'
    | 'RegistrationLink'
  taggable_uuid: string
}

export interface UpdateTagRequest {
  name?: string
  color?: string
}

export interface CreateTagResponse {
  data: Tag
}

// Funções para integração com a API v1

export const getTagsRequest = async (params?: {
  page?: number
  per_page?: number
  search?: string
  filter_by?: string
}): Promise<TagsResponse> => {
  let url = '/v1/tags'

  // Adicionar parâmetros de filtro na URL
  if (params?.filter_by) {
    url += `?filter_by=${encodeURIComponent(
      params.filter_by.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    )}`
  } else if (params?.search) {
    const searchValue = params.search
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '+')
    url += `?filter_by=${encodeURIComponent(`name ${searchValue}`)}`
  }

  const response = await DataService({
    url,
    type: 'GET'
  })

  // Verificar se houve erro
  if (response.error) {
    throw new Error(response.message || 'Erro ao buscar tags')
  }

  // A API pode retornar dados em response.data.records ou response.data diretamente
  let tagsData = []
  if (response.data?.records) {
    tagsData = response.data.records
  } else if (response.data?.data && Array.isArray(response.data.data)) {
    tagsData = response.data.data
  } else if (Array.isArray(response.data)) {
    tagsData = response.data
  } else {
    console.warn('Formato de resposta inesperado:', response.data)
    tagsData = []
  }

  const tags = tagsData.map(convertTagAPIToTag)

  return {
    data: tags,
    total: tags.length,
    page: params?.page || 1,
    per_page: params?.per_page || 50
  }
}

export const createTagRequest = async (
  data: CreateTagRequest
): Promise<CreateTagResponse> => {
  // Remover # da cor antes de enviar para a API
  const apiData = {
    ...data,
    color: data.color ? removeHashFromColor(data.color) : undefined
  }

  const response = await DataService({
    url: '/v1/tags',
    type: 'POST',
    data: apiData
  })

  // Verificar se houve erro
  if (response.error) {
    throw new Error(apiErrorMessage(response, 'Erro ao criar tag'))
  }

  // A API pode retornar dados em response.data.record, response.data.data ou response.data diretamente
  let tagData = null
  if (response.data?.record) {
    tagData = response.data.record
  } else if (response.data?.data) {
    tagData = response.data.data
  } else {
    tagData = response.data
  }

  return {
    data: convertTagAPIToTag(tagData)
  }
}

export const createTagWithLinkRequest = async (
  data: CreateTagWithLinkRequest
): Promise<CreateTagResponse> => {
  // Remover # da cor antes de enviar para a API
  const apiData = {
    name: data.name,
    color: data.color ? removeHashFromColor(data.color) : undefined,
    taggable_type: data.taggable_type,
    taggable_uuid: data.taggable_uuid
  }

  const response = await DataService({
    url: '/v1/tags',
    type: 'POST',
    data: apiData
  })

  // Verificar se houve erro
  if (response.error) {
    throw new Error(apiErrorMessage(response, 'Erro ao criar e vincular tag'))
  }

  // A API pode retornar dados em response.data.record, response.data.data ou response.data diretamente
  let tagData = null
  if (response.data?.record) {
    tagData = response.data.record
  } else if (response.data?.data) {
    tagData = response.data.data
  } else {
    tagData = response.data
  }

  const createdTag = convertTagAPIToTag(tagData)

  return {
    data: createdTag
  }
}

export const updateTagRequest = async (
  uuid: string,
  data: UpdateTagRequest
): Promise<CreateTagResponse> => {
  // Remover # da cor antes de enviar para a API
  const apiData = {
    ...data,
    color: data.color ? removeHashFromColor(data.color) : undefined
  }

  const response = await DataService({
    url: `/v1/tags/${uuid}`,
    type: 'PATCH',
    data: apiData
  })

  // Verificar se houve erro
  if (response.error) {
    throw new Error(apiErrorMessage(response, 'Erro ao atualizar tag'))
  }

  // A API pode retornar dados em response.data.record, response.data.data ou response.data diretamente
  let tagData = null
  if (response.data?.record) {
    tagData = response.data.record
  } else if (response.data?.data) {
    tagData = response.data.data
  } else {
    tagData = response.data
  }

  return {
    data: convertTagAPIToTag(tagData)
  }
}

export const deleteTagRequest = async (
  uuid: string,
  force = false
): Promise<void> => {
  const response = await DataService({
    url: force ? `/v1/tags/${uuid}?force=true` : `/v1/tags/${uuid}`,
    type: 'DELETE'
  })

  // Verificar se houve erro
  if (response.error) {
    throw new Error(apiErrorMessage(response, 'Erro ao excluir tag'))
  }
}

export interface TagUsage {
  in_use: boolean
  total: number
  by_type: Record<string, number>
}

export const getTagUsageRequest = async (uuid: string): Promise<TagUsage> => {
  const response = await DataService({
    url: `/v1/tags/${uuid}/usage`,
    type: 'GET'
  })

  if (response.error) {
    throw new Error(response.message || 'Erro ao buscar uso da tag')
  }

  const record = response.data?.record ?? response.data
  return {
    in_use: Boolean(record?.in_use),
    total: record?.total ?? 0,
    by_type: record?.by_type ?? {}
  }
}

export const getTagRequest = async (
  uuid: string
): Promise<CreateTagResponse> => {
  const response = await DataService({
    url: `/v1/tags/${uuid}`,
    type: 'GET'
  })

  // Verificar se houve erro
  if (response.error) {
    throw new Error(response.message || 'Erro ao buscar tag')
  }

  // A API pode retornar dados em response.data.record, response.data.data ou response.data diretamente
  let tagData = null
  if (response.data?.record) {
    tagData = response.data.record
  } else if (response.data?.data) {
    tagData = response.data.data
  } else {
    tagData = response.data
  }

  return {
    data: convertTagAPIToTag(tagData)
  }
}

// Interfaces para link/unlink de tags
export interface TagLinkRequest {
  taggable_type:
    | 'Customer'
    | 'Billing'
    | 'Recurrence'
    | 'ProductLink'
    | 'Product'
    | 'AccountsPayable'
    | 'AccountsPayableRecurrence'
    | 'Installment'
    | 'RegisterLink'
  taggable_uuid: string
  tag_name?: string
  tag_color?: string
}

export interface TagUnlinkRequest {
  taggable_type:
    | 'Customer'
    | 'Billing'
    | 'Recurrence'
    | 'ProductLink'
    | 'Product'
    | 'AccountsPayable'
    | 'AccountsPayableRecurrence'
    | 'Installment'
    | 'RegisterLink'
  taggable_uuid: string
}

// Função para vincular uma tag a uma entidade
export const linkTagToEntity = async (
  tagUuid: string,
  payload: TagLinkRequest
): Promise<void> => {
  if (!tagUuid) {
    console.error('linkTagToEntity: tagUuid is undefined or empty')
    throw new Error(
      'Para criar uma nova tag, o parâmetro tag_name é obrigatório.'
    )
  }

  // Preparar dados para envio - remover # da cor se presente
  const requestData = {
    ...payload
  }

  const url = `/v1/tags/${tagUuid}/link`
  const response = await DataService({
    url,
    type: 'POST',
    data: requestData
  })

  if (response.error) {
    throw new Error(apiErrorMessage(response, 'Erro ao vincular tag'))
  }
}

// Função para desvincular uma tag de uma entidade
export const unlinkTagFromEntity = async (
  tagUuid: string,
  payload: TagUnlinkRequest
): Promise<void> => {
  const response = await DataService({
    url: `/v1/tags/${tagUuid}/unlink`,
    type: 'DELETE',
    data: payload
  })

  if (response.error) {
    throw new Error(apiErrorMessage(response, 'Erro ao desvincular tag'))
  }
}

// Enhanced API functions with debouncing and optimistic locking

export interface OptimisticLockData {
  version?: string
}

export interface LinkTagRealtimeRequest
  extends TagLinkRequest,
    OptimisticLockData {}
export interface UnlinkTagRealtimeRequest
  extends TagUnlinkRequest,
    OptimisticLockData {}

// Response types for realtime operations
export interface RealtimeOperationResponse {
  success: boolean
  message?: string
  version?: string
}

// Error type for API operations
export interface TagOperationError extends Error {
  status?: number
  code?: string
}

// Enhanced link function with optimistic locking
export const linkTagRealtimeOperation = async (
  tagUuid: string,
  payload: LinkTagRealtimeRequest
): Promise<RealtimeOperationResponse> => {
  const response = await DataService({
    url: `/v1/tags/${tagUuid}/link`,
    type: 'POST',
    data: {
      ...payload,
      version: payload.version
    }
  })

  if (response.error) {
    const error = new Error(
      response.message || 'Erro ao vincular tag'
    ) as TagOperationError
    error.status = response.status
    error.code = response.error
    throw error
  }

  return {
    success: true,
    message: response.data?.message || 'Tag vinculada com sucesso',
    version: response.data?.version
  }
}

// Enhanced unlink function with optimistic locking
export const unlinkTagRealtimeOperation = async (
  tagUuid: string,
  payload: UnlinkTagRealtimeRequest
): Promise<RealtimeOperationResponse> => {
  const response = await DataService({
    url: `/v1/tags/${tagUuid}/unlink`,
    type: 'DELETE',
    data: {
      ...payload,
      version: payload.version
    }
  })

  if (response.error) {
    const error = new Error(
      response.message || 'Erro ao desvincular tag'
    ) as TagOperationError
    error.status = response.status
    error.code = response.error
    throw error
  }

  return {
    success: true,
    message: response.data?.message || 'Tag desvinculada com sucesso',
    version: response.data?.version
  }
}
